import Sentry from "@similarweb/sentry-logger";

import type { StartupSettings } from "app/@types/startupSettings.types";
import type { UserDataConfig } from "app/@types/userdata";

const RESPONSE_BODY_PREVIEW_LENGTH = 200;
const SENTRY_REPORTED = Symbol("sentryReported");

type SentryReportedCarrier = { [SENTRY_REPORTED]?: boolean };

export class ResponseBodyError extends Error {
    readonly status: number;
    readonly statusText: string;
    readonly contentType: string | null;
    readonly bodyPreview: string;
    readonly bodyLength: number;
    readonly url: string;
    readonly retryable: boolean;

    constructor(params: {
        message: string;
        status: number;
        statusText: string;
        contentType: string | null;
        bodyPreview: string;
        bodyLength: number;
        url: string;
        retryable: boolean;
        cause?: unknown;
    }) {
        super(params.message, params.cause !== undefined ? { cause: params.cause } : undefined);
        this.name = "ResponseBodyError";
        this.status = params.status;
        this.statusText = params.statusText;
        this.contentType = params.contentType;
        this.bodyPreview = params.bodyPreview;
        this.bodyLength = params.bodyLength;
        this.url = params.url;
        this.retryable = params.retryable;
    }

    get sentryExtra(): Record<string, unknown> {
        return {
            responseStatus: this.status,
            responseStatusText: this.statusText,
            contentType: this.contentType,
            responseTextLength: this.bodyLength,
            responseTextPreview: this.bodyPreview,
            url: this.url,
            retryable: this.retryable,
            ...(this.cause instanceof ResponseBodyError ? { firstAttempt: this.cause.sentryExtra } : {}),
        };
    }
}

export const markSentryReported = (error: unknown): void => {
    if (error && typeof error === "object") {
        (error as SentryReportedCarrier)[SENTRY_REPORTED] = true;
    }
};

export const wasSentryReported = (error: unknown): boolean => {
    return Boolean(error && typeof error === "object" && (error as SentryReportedCarrier)[SENTRY_REPORTED]);
};

export const reportFatalOnce = (error: unknown, message: string): void => {
    if (wasSentryReported(error)) {
        return;
    }
    Sentry.errorTracker({
        message,
        e: error,
        level: "fatal",
        ...(error instanceof ResponseBodyError ? { params: { extra: error.sentryExtra } } : {}),
    });
    markSentryReported(error);
};

const previewBody = (responseText: string): string => {
    return responseText.substring(0, RESPONSE_BODY_PREVIEW_LENGTH);
};

const isRetryableNetworkError = (error: unknown): boolean => {
    if (!(error instanceof TypeError)) {
        return false;
    }
    const message = error.message.toLowerCase();
    return (
        message.includes("failed to fetch") ||
        message.includes("networkerror") ||
        message.includes("network error") ||
        message.includes("load failed")
    );
};

export const isRetryableStartupSettingsError = (error: unknown): boolean => {
    if (error instanceof ResponseBodyError) {
        return error.retryable;
    }
    return isRetryableNetworkError(error);
};

export const withResponseBody = async <T = unknown>(promise?: Promise<Response>): Promise<T> => {
    if (!promise) {
        throw new Error("No promise provided to withResponseBody");
    }
    const res = await promise;
    const contentType = res.headers.get("content-type");
    const url = res.url;

    let responseText = "";
    try {
        responseText = await res.text();
    } catch (error) {
        throw new ResponseBodyError({
            message: "Failed to read response body",
            status: res.status,
            statusText: res.statusText,
            contentType,
            bodyPreview: "",
            bodyLength: 0,
            url,
            retryable: true,
            cause: error,
        });
    }

    if (!res.ok) {
        throw new ResponseBodyError({
            message: `Request failed with status ${res.status}`,
            status: res.status,
            statusText: res.statusText,
            contentType,
            bodyPreview: previewBody(responseText),
            bodyLength: responseText.length,
            url,
            retryable: res.status >= 500,
        });
    }

    if (responseText.length === 0) {
        throw new ResponseBodyError({
            message: "Response body is empty",
            status: res.status,
            statusText: res.statusText,
            contentType,
            bodyPreview: "",
            bodyLength: 0,
            url,
            retryable: true,
        });
    }

    try {
        return JSON.parse(responseText) as T;
    } catch (error) {
        throw new ResponseBodyError({
            message: "Response is not valid JSON",
            status: res.status,
            statusText: res.statusText,
            contentType,
            bodyPreview: previewBody(responseText),
            bodyLength: responseText.length,
            url,
            retryable: false,
            cause: error,
        });
    }
};

export const loadStartupSettings = async <T>(
    initialPromise: Promise<Response> | undefined,
    refetch: () => Promise<Response>,
): Promise<T> => {
    try {
        return await withResponseBody<T>(initialPromise);
    } catch (firstError) {
        if (!isRetryableStartupSettingsError(firstError)) {
            throw firstError;
        }
        try {
            return await withResponseBody<T>(refetch());
        } catch (retryError) {
            if (retryError instanceof Error && retryError.cause === undefined) {
                retryError.cause = firstError;
            }
            throw retryError;
        }
    }
};

/**
 * This function is used to wrap async functions and report errors to Sentry.
 * It will also return a fallback value if provided.
 * @param func - The async function to wrap
 * @param config - The configuration object
 * @param config.errorDescription - The description of the error to report to Sentry
 * @param config.returnFallbackValue - The fallback value to return if an error occurs
 * @param config.reportError - Whether to report the error to Sentry
 * @returns The response of the async function
 * @throws The error that occurred in the async function
 * @example
 * const response = await asyncReportWrapper(fetch("https://api.example.com"));
 * @example
 * const response = await asyncReportWrapper(fetch("https://api.example.com"), {
 *    errorDescription: "Error fetching data",
 *   returnFallbackValue: [],
 *  reportError: true,
 * });
 */
export const asyncReportWrapper = async <T>(
    func: Promise<T>,
    config: { errorDescription?: string; returnFallbackValue?: T; consoleOnly?: boolean } = {},
) => {
    const { errorDescription, returnFallbackValue, consoleOnly = false } = config ?? {};
    try {
        const response = await func;
        return response;
    } catch (error) {
        const errorMessage = `${errorDescription ?? "Error in asyncReportWrapper"} - ${error instanceof Error ? error.message : String(error)}`;
        if (!consoleOnly) {
            reportFatalOnce(error, errorMessage);
        } else {
            console.error(errorMessage, error);
        }

        if (returnFallbackValue) {
            return returnFallbackValue;
        }

        throw error;
    }
};

/**
 * Analyzes network errors to provide better debugging information
 */
const analyzeNetworkError = (error: Error, input: RequestInfo | URL) => {
    const errorMessage = error.message?.toLowerCase() || "";
    const errorName = error.name?.toLowerCase() || "";

    // Determine error category based on error patterns
    let category = "UNKNOWN_NETWORK_ERROR";
    let isNetworkIssue = true;
    const diagnostics: Record<string, unknown> = {
        errorName: error.name,
        errorMessage: error.message,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        onlineStatus: typeof navigator !== "undefined" ? navigator.onLine : "unknown",
        timestamp: new Date().toISOString(),
    };

    // Connection-related errors
    if (errorMessage.includes("failed to fetch") || errorMessage.includes("network error")) {
        if (typeof navigator !== "undefined" && !navigator.onLine) {
            category = "OFFLINE";
            diagnostics.likely_cause = "User is offline or has no internet connection";
        } else {
            category = "CONNECTION_FAILED";
            diagnostics.likely_cause = "Network connection failed - could be DNS, firewall, or server unavailable";
        }
    }
    // CORS errors
    else if (errorMessage.includes("cors") || errorMessage.includes("cross-origin")) {
        category = "CORS_ERROR";
        isNetworkIssue = false;
        diagnostics.likely_cause = "Cross-origin request blocked by browser";
    }
    // Timeout errors
    else if (errorMessage.includes("timeout") || errorName.includes("timeout")) {
        category = "TIMEOUT";
        diagnostics.likely_cause = "Request timed out - slow connection or server response";
    }
    // AbortController errors
    else if (errorName.includes("abort") || errorMessage.includes("abort")) {
        category = "REQUEST_ABORTED";
        isNetworkIssue = false;
        diagnostics.likely_cause = "Request was deliberately cancelled";
    }
    // SSL/TLS errors
    else if (errorMessage.includes("ssl") || errorMessage.includes("tls") || errorMessage.includes("certificate")) {
        category = "SSL_ERROR";
        diagnostics.likely_cause = "SSL/TLS certificate error or security issue";
    }
    // DNS errors
    else if (errorMessage.includes("dns") || errorMessage.includes("name not resolved")) {
        category = "DNS_ERROR";
        diagnostics.likely_cause = "Cannot resolve domain name - DNS issue";
    }

    // Add URL analysis
    const url = typeof input === "string" ? input : (input as URL)?.href || (input as Request)?.url || "unknown";
    diagnostics.targetUrl = url;
    try {
        diagnostics.targetDomain = url !== "unknown" ? new URL(url).hostname : "unknown";
    } catch {
        diagnostics.targetDomain = "invalid-url";
    }

    // Add browser/environment context
    if (typeof globalThis !== "undefined" && globalThis.window?.location) {
        diagnostics.windowLocation = globalThis.window.location.href;
        diagnostics.referrer = typeof globalThis.document !== "undefined" ? globalThis.document.referrer : "unknown";

        // Check if page is currently hidden (switched to another tab)
        if (typeof globalThis.document !== "undefined") {
            diagnostics.pageVisibilityState = globalThis.document.visibilityState;
            diagnostics.pageHidden = globalThis.document.hidden;
        }
    }

    return {
        category,
        isNetworkIssue,
        diagnostics,
    };
};

/**
 * Robust JSON fetcher that reads response as text once, reports detailed errors to Sentry,
 * and returns a safe fallback value on failure.
 */
export const fetchJsonWithSentry = async <T>(
    input: RequestInfo | URL,
    options: { fallbackValue: T; resourceName: string },
): Promise<T> => {
    const { fallbackValue, resourceName } = options;
    try {
        const res = await fetch(input);

        if (res.status === 304) {
            // 304 Not Modified, but fetch does not expose the cached body.
            // Return a fallback to avoid parsing an empty response and reduce Sentry noise.
            return fallbackValue;
        }

        let responseText = "";
        try {
            // Read the response body as text once (safe even if status is not 200)
            responseText = await res.text();
        } catch (error) {
            Sentry.errorTracker({
                message: `Failed to read response text from ${resourceName}`,
                e: error,
                params: {
                    extra: {
                        responseStatus: res.status,
                        url: res.url,
                    },
                },
            });
            return fallbackValue;
        }

        if (!res.ok) {
            // Log error details and skip parsing
            const message = `${resourceName} returned error HTTP status: ${res.status}`;
            Sentry.errorTracker({
                e: new Error(message),
                message,
                params: {
                    extra: {
                        responseStatus: res.status,
                        url: res.url,
                        responseTextPreview: responseText?.substring(0, 500) || "",
                        responseTextSuffix:
                            responseText.length > 500 ? responseText.substring(responseText.length - 100) : "",
                    },
                },
            });
            return fallbackValue;
        }

        try {
            return JSON.parse(responseText) as T;
        } catch (error) {
            Sentry.errorTracker({
                message: `Failed to parse JSON from ${resourceName}`,
                e: error,
                params: {
                    extra: {
                        responseStatus: res.status,
                        responseStatusText: res.statusText,
                        contentType: res.headers.get("content-type"),
                        contentLength: res.headers.get("content-length"),
                        responseTextLength: responseText.length,
                        responseTextPreview: responseText.substring(0, 500),
                        responseTextSuffix:
                            responseText.length > 500 ? responseText.substring(responseText.length - 100) : "",
                        url: res.url,
                    },
                },
            });
            return fallbackValue;
        }
    } catch (error) {
        const errorDetails = analyzeNetworkError(error as Error, input);

        // Don't spam Sentry with transient network errors that are gracefully handled
        const shouldSkipSentry =
            errorDetails.isNetworkIssue &&
            (errorDetails.category === "CONNECTION_FAILED" ||
                errorDetails.category === "OFFLINE" ||
                errorDetails.category === "TIMEOUT") &&
            // Skip if page is hidden (user switched tabs, browser suspended network)
            (errorDetails.diagnostics.pageHidden === true || errorDetails.diagnostics.pageVisibilityState === "hidden");

        if (!shouldSkipSentry) {
            Sentry.errorTracker({
                message: `${errorDetails.category}: Error fetching ${resourceName}`,
                e: error,
                params: {
                    extra: {
                        ...errorDetails.diagnostics,
                        input: typeof input === "string" ? input : (input as URL)?.href || (input as Request)?.url,
                        errorCategory: errorDetails.category,
                        networkIssue: errorDetails.isNetworkIssue,
                    },
                    tags: {
                        errorCategory: errorDetails.category,
                        networkIssue: errorDetails.isNetworkIssue,
                    },
                },
            });
        }
        return fallbackValue;
    }
};

export const reportWrapper = <T>(func: () => T, errorDescription?: string) => {
    try {
        const value = func();
        return value;
    } catch (error) {
        Sentry.errorTracker({
            message: `${errorDescription ?? "Error in reportWrapper"} - ${error instanceof Error ? error.message : String(error)}`,
            e: error,
            level: "fatal",
        });

        throw error;
    }
};

export const initWindowSettings = (data: StartupSettings) => {
    const { settings, categories, countries } = data;
    if (settings) {
        Object.values(settings.components).forEach((component) => {
            component.full = false;
        });
        window.similarweb.settings = settings;
    } else {
        Sentry.errorTracker({
            message: "Error in initWindow - No settings provided in initWindow",
            e: new Error("No settings provided in initWindow"),
            level: "fatal",
        });
    }

    if (categories) {
        window.similarweb.config.categories = categories;
    } else {
        Sentry.errorTracker({
            message: "Error in initWindow - No categories provided in initWindow",
            e: new Error("No categories provided in initWindow"),
            level: "fatal",
        });
    }

    if (countries) {
        window.similarweb.config.countries = countries.countriesClient;
        window.similarweb.config.mobileUsersCountries = countries.mobileUsersCountries;
        window.similarweb.config.regions = countries.regions;
    } else {
        Sentry.errorTracker({
            message: "Error in initWindow - No countries provided in initWindow",
            e: new Error("No countries provided in initWindow"),
            level: "fatal",
        });
    }
};

function setWindowDataRecursive(
    data: unknown,
    path: string[],
    node: Record<string, unknown> = window as unknown as Record<string, unknown>,
) {
    if (path.length === 0) {
        return;
    }
    const [currentKey, ...restPath] = path;
    if (restPath.length === 0) {
        node[currentKey] = data;
    } else {
        if (!node[currentKey]) {
            node[currentKey] = {};
        }
        setWindowDataRecursive(data, restPath, node[currentKey] as Record<string, unknown>);
    }
}

export const updateWindowUser = (key: keyof UserDataConfig, data: UserDataConfig[typeof key]) => {
    setWindowDataRecursive(data, ["similarweb", "config", "userData", key]);
};

export const initWindowUser = (userData: UserDataConfig) => {
    if (userData) {
        setWindowDataRecursive(userData, ["similarweb", "config", "userData"]);
    } else {
        Sentry.errorTracker({
            message: "Error in initWindow - No userData provided in initWindow",
            e: new Error("No userData provided in initWindow"),
            level: "fatal",
        });
    }
};
